import React, { useMemo } from "react";

import { faTable } from "@fortawesome/pro-regular-svg-icons";

import { PYColumnDef, Table } from "@performyard/py-components";

import { OutputCard } from "./OutputCard";
import { SaveOutputControl } from "./SaveOutputControl";

import type { TableOutput } from "../types";

type Row = Record<string, unknown>;

const humanize = (key: string): string =>
	key
		.replace(/[_.]+/g, " ")
		.replace(/\b\w/g, (c) => c.toUpperCase())
		.trim();

const formatCell = (value: unknown): string => {
	if (value === null || value === undefined || value === "") return "-";
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
};

// Inline data table via the py-components (ag-grid) Table wrapper. Columns are derived from the
// real row keys (summarized rows can carry keys the column list doesn't name), with labels
// prettified from the query's column defs when a matching id exists.
export const InlineTableBubble: React.FC<{ output: TableOutput; defaultName: string }> = ({ output, defaultName }) => {
	const columnDefs = useMemo<PYColumnDef<Row>[]>(() => {
		const labelById = new Map(output.columns.map((column) => [column.id, column.label]));
		const keys = output.rows.length > 0 ? Object.keys(output.rows[0]) : output.columns.map((column) => column.id);
		return keys.map((key) => ({
			colId: key,
			headerName: labelById.get(key) || humanize(key),
			valueGetter: (params) => formatCell(params.data?.[key]),
			sortable: true,
			resizable: true,
			flex: 1,
			minWidth: 120,
		}));
	}, [output.columns, output.rows]);

	return (
		<OutputCard
			icon={faTable}
			title={output.title}
			headerRight={<SaveOutputControl output={output} defaultName={defaultName} />}
		>
			<div className="max-h-[420px] overflow-auto">
				<Table<Row>
					autoHeight
					rowData={output.rows}
					columnDefs={columnDefs}
					emptyStateText="No rows matched this query."
					animateRows={false}
					onGridReady={(params) => params.api.sizeColumnsToFit()}
					onGridSizeChanged={(params) => params.api.sizeColumnsToFit()}
				/>
			</div>
			<p className="mt-2 text-xs text-neutral-400">
				{output.truncated
					? "Showing the first 100 rows — narrow the query to see the rest."
					: `${output.rowCount} ${output.rowCount === 1 ? "row" : "rows"}`}
			</p>
		</OutputCard>
	);
};

export default InlineTableBubble;
